'use client';

/**
 * GenerativeArtistAvatar — vectorised, theme-aware fallback thumbnail.
 *
 * Used wherever an artist (or release / user) has no uploaded image. Renders
 * an SVG (not canvas) so it is crisp at any size, follows the active theme
 * via CSS variables, and animates slowly using native SMIL — no JS RAF loop,
 * no DOM thrash, no audio context.
 *
 * Visual spec (the user has asked for this exactly):
 *   • Artist name in IMPACT (condensed) at the largest possible size with very
 *     little padding — auto-stretched to fill width via SVG `textLength` +
 *     `lengthAdjust="spacingAndGlyphs"` so it always looks confident.
 *   • Theme-coloured background gradient driven by `hsl(var(--primary))` and
 *     `hsl(var(--accent))` — automatically reflects every theme & dark mode.
 *   • Slow background gradient rotation + two drifting accent shapes, all
 *     vector. 30–60 second cycles so it never feels jittery.
 *
 * The export name + Props interface match the legacy canvas component so every
 * caller (artists/, home, labels, profile, music pages) upgrades for free.
 */

import { useId, useMemo, useRef } from 'react';
import { useAvatarReactivity } from '@/hooks/use-avatar-reactivity';

interface Props {
  name: string;
  size?: number;            // px (used for sizing; SVG is responsive when in className)
  className?: string;
  animate?: boolean;        // false → static first frame
  /** Which pattern composition to draw under the name. 0..PATTERN_NAMES.length-1.
   *  Omit or pass -1 for the auto pick (seeded from the artist name). */
  patternOverride?: number;
  /** Background fill. Pass `"transparent"` (default) to let the page background
   *  show through, `"theme"` to use hsl(var(--background)), or any CSS colour
   *  string (e.g. `#0d1117`, `rgb(...)`, `hsl(...)`). */
  bgColor?: string;
  /** Stroke + text colour. Default `hsl(var(--foreground))`. Only really useful
   *  when bgColor is set to a custom value that needs a matching line colour. */
  fgColor?: string;
  /** CSS font family for the name lettering (Google font or a cloud custom
   *  font family — the caller/customizer is responsible for loading it).
   *  Falls back to the Impact stack. */
  fontFamily?: string;
  // Legacy prop kept for back-compat — accepted, ignored.
  analyserNode?: unknown;
  paletteOverride?: number;
}

/** Names match the renderPattern() switch below — keep in lockstep when adding.
 *  Patterns 0–5 are still abstract geometric. 6+ are explicitly musical and
 *  all of them react to the live media-player audio (8-band frequency split +
 *  smoothed energy) and to device tilt (mobile DeviceOrientation, mouse on
 *  desktop). Reactivity is driven by `useAvatarReactivity()` which writes
 *  CSS custom properties (--bb-energy, --bb-tilt-x/y, --bb-band-0..7) onto
 *  the SVG — patterns read them via inline `calc()` in transform/scale/opacity. */
export const PATTERN_NAMES = [
  'Concentric Rings',
  'Drift Arcs',
  'Triangle Field',
  'Wave Lines',
  'Cross-hatch',
  'Constellation',
  // Musical, audio-reactive:
  'EQ Bars',
  'Vinyl Record',
  'Piano Keys',
  'Frequency Rings',
  'Cassette Reels',
  'Drum Pads',
  'Sound Wave',
  'Treble Clef',
  'Mixer Faders',
  'Oscilloscope',
  'Speaker Cone',
  'Note Constellation',
] as const;

/** Kept for back-compat — UI no longer uses preset palettes, every colour is
 *  either theme tokens or a free-form picker. */
export const PALETTES: [string, string, string][] = [
  ['hsl(var(--primary))', 'hsl(var(--accent))', 'hsl(var(--foreground))'],
];

/* ── Name → lines (keeps text big, balanced, never tiny) ──────────────────── */
function splitLines(raw: string): string[] {
  const name = (raw || '?').trim().toUpperCase();
  if (!name) return ['?'];
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length <= 1) return [name];
  if (words.length === 2) return words;
  if (words.length === 3) {
    const a = [words[0], words.slice(1).join(' ')];
    const b = [words.slice(0, 2).join(' '), words[2]];
    // Pick the more balanced split (smaller max line length).
    return Math.max(a[0].length, a[1].length) <= Math.max(b[0].length, b[1].length) ? a : b;
  }
  // 4+ words: two roughly equal halves.
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
}

/* Deterministic small hash so the per-artist accent offsets are stable. */
function hashName(name: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < name.length; i++) {
    h ^= name.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return h;
}

/** Resolve the `bgColor` prop:
 *   "transparent" → no fill (page bg shows through)
 *   "theme"       → hsl(var(--background))
 *   anything else → pass through verbatim (e.g. "#0d1117", "rgb(…)", "hsl(…)")
 *   undefined     → "transparent" (default)
 */
function resolveBg(bg: string | undefined): string {
  if (!bg || bg === 'transparent') return 'transparent';
  if (bg === 'theme') return 'hsl(var(--background))';
  return bg;
}

export function GenerativeArtistAvatar({
  name,
  size = 200,
  className = '',
  animate = true,
  patternOverride,
  bgColor,
  fgColor,
  fontFamily,
}: Props) {
  const uid = useId().replace(/:/g, '');
  const svgRef = useRef<SVGSVGElement>(null);
  // Subscribe this SVG to the shared tilt + audio source. The hook writes
  // CSS vars directly onto the element via RAF — no React re-renders per frame.
  useAvatarReactivity(svgRef);

  const { lines, fontSize, ys, seed } = useMemo(() => {
    const lines = splitLines(name);
    const padding = 6;
    const blockH = 38;
    const lineH = blockH / lines.length;
    const fontSize = lineH * 0.95;
    const ys = lines.map((_, i) => padding + (i + 1) * lineH - (lineH - fontSize * 0.78));
    return { lines, fontSize, ys, seed: hashName(name || '?') };
  }, [name]);

  // Pattern selection — explicit override wins, otherwise seeded from name so
  // two adjacent fallbacks still look different.
  const rawPatternIndex = (patternOverride !== undefined && patternOverride >= 0)
    ? patternOverride % PATTERN_NAMES.length
    : seed % PATTERN_NAMES.length;
  // Treble Clef (13) is HIDDEN until it can show the track's REAL notes — its
  // note heads were decorative, which reads as a wrong transcription (Gordon:
  // "should show the notes of the track; if not, hide it until solved").
  // Both seeded picks and explicit overrides fall back to Note Constellation.
  const patternIndex = rawPatternIndex === 13 ? 17 : rawPatternIndex;

  // Per-artist offsets so two artists on the same pattern don't look identical.
  const cx1 = 22 + (seed % 18);
  const cy1 = 58 + ((seed >> 4) % 16);
  const cx2 = 78 - (seed % 16);
  const cy2 = 78 - ((seed >> 6) % 14);

  const fontStack = fontFamily
    ? `'${fontFamily}', Impact, 'Haettenschweiler', 'Franklin Gothic Heavy', 'Arial Narrow', 'Oswald', sans-serif`
    : `Impact, 'Haettenschweiler', 'Franklin Gothic Heavy', 'Arial Narrow', 'Oswald', sans-serif`;
  const bg = resolveBg(bgColor);
  // Stroke / text colour. Default is theme foreground; admins picking a custom
  // bgColor can pass an explicit fgColor that matches.
  const stroke = fgColor || 'hsl(var(--foreground))';
  // Text stroke (1px ring around glyphs) needs to be the inverse of the fill
  // so the name reads on any custom bg. Mirror that with the bg colour.
  const textHaloStroke = bg === 'transparent' ? 'hsl(var(--background))' : bg;

  // ── Pattern compositions (all stroke-only, no fills, theme-aware) ─────
  const renderPattern = (): React.ReactNode => {
    // Heavier strokes + soft glow so the vector art reads clearly, and a
    // WHOLE-PATTERN woofer zoom driven by the bass band so the audio
    // reactivity is unmistakable on every pattern (Gordon: "can't see
    // woofer effect zoom in on sound").
    const g = (children: React.ReactNode) => (
      <g stroke={stroke} fill="none" strokeWidth={1.1} opacity={0.85}
        strokeLinecap="round" filter={`url(#pglow-${uid})`}
        style={{
          transformBox: 'fill-box',
          transformOrigin: '50% 75%',
          transform: 'scale(calc(1 + var(--bb-band-0, 0) * 0.07 + var(--bb-energy, 0) * 0.03))',
        }}>
        {children}
      </g>
    );
    switch (patternIndex) {
      case 0: // Concentric Rings
        return g(<>
          {[10, 18, 26].map((r, i) => (
            <circle key={i} cx={cx1} cy={cy1} r={r}>
              {animate && <>
                <animate attributeName="cx" values={`${cx1};${100 - cx1};${cx1}`} dur={`${42 + i * 6}s`} repeatCount="indefinite" />
                <animate attributeName="cy" values={`${cy1};${cy1 + 6};${cy1}`} dur={`${55 + i * 4}s`} repeatCount="indefinite" />
              </>}
            </circle>
          ))}
          <circle cx={cx2} cy={cy2} r="9">
            {animate && <>
              <animate attributeName="cx" values={`${cx2};${100 - cx2};${cx2}`} dur="48s" repeatCount="indefinite" />
              <animate attributeName="cy" values={`${cy2};${cy2 - 6};${cy2}`} dur="51s" repeatCount="indefinite" />
            </>}
          </circle>
        </>);
      case 1: // Drift Arcs
        return g(<>
          <path d={`M 6 ${cy2} Q 50 ${cy2 - 14} 94 ${cy2 + 4}`} />
          <path d={`M 6 ${cy2 + 8} Q 50 ${cy2 + 22} 94 ${cy2 + 12}`} opacity="0.6" />
          <path d={`M 10 ${cy1 + 6} Q 50 ${cy1 - 10} 90 ${cy1 + 2}`} opacity="0.8" />
          <circle cx={cx2} cy={cy2} r="11" />
        </>);
      case 2: // Triangle Field
        return g(<>
          {Array.from({ length: 7 }).map((_, i) => {
            const x = 10 + (i * 12) + ((seed >> i) % 5);
            const y = 60 + ((seed >> (i * 2)) % 30);
            const r = 5 + ((seed >> (i + 4)) % 6);
            return <polygon key={i} points={`${x},${y - r} ${x + r * 0.85},${y + r * 0.6} ${x - r * 0.85},${y + r * 0.6}`} />;
          })}
        </>);
      case 3: // Wave Lines
        return g(<>
          {[60, 70, 80, 90].map((y, i) => (
            <path key={i} d={`M 4 ${y} Q 30 ${y - 5} 50 ${y} T 96 ${y}`} opacity={0.4 + i * 0.15}>
              {animate && <animate attributeName="d"
                values={`M 4 ${y} Q 30 ${y - 5} 50 ${y} T 96 ${y};M 4 ${y} Q 30 ${y + 5} 50 ${y} T 96 ${y};M 4 ${y} Q 30 ${y - 5} 50 ${y} T 96 ${y}`}
                dur={`${10 + i * 3}s`} repeatCount="indefinite" />}
            </path>
          ))}
        </>);
      case 4: // Cross-hatch
        return g(<>
          {Array.from({ length: 9 }).map((_, i) => {
            const x = 10 + i * 10;
            return <line key={`a-${i}`} x1={x} y1="50" x2={x + 8} y2="98" opacity={0.4 + (i % 3) * 0.1} />;
          })}
          {Array.from({ length: 9 }).map((_, i) => {
            const x = 10 + i * 10;
            return <line key={`b-${i}`} x1={x} y1="98" x2={x + 8} y2="50" opacity={0.4 + ((i + 2) % 3) * 0.1} />;
          })}
        </>);
      case 5: // Constellation
        return g(<>
          {[...Array(7)].map((_, i) => {
            const x = 8 + ((seed >> i) % 86);
            const y = 55 + ((seed >> (i + 2)) % 42);
            return <circle key={i} cx={x} cy={y} r="1.2" fill={stroke} stroke="none" />;
          })}
          {[...Array(5)].map((_, i) => {
            const x1 = 8 + ((seed >> i) % 86);
            const y1 = 55 + ((seed >> (i + 2)) % 42);
            const x2 = 8 + ((seed >> (i + 1)) % 86);
            const y2 = 55 + ((seed >> (i + 3)) % 42);
            return <line key={`l-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} opacity="0.45" />;
          })}
        </>);

      // ── Musical patterns (audio + tilt reactive) ──────────────────────
      // Each pattern reads CSS vars set by useAvatarReactivity:
      //   var(--bb-energy)      0..1  smoothed RMS
      //   var(--bb-band-N)      0..1  per-band level (8 bands)
      //   var(--bb-tilt-x/y)    -1..1 device tilt / mouse offset

      case 6: { // EQ Bars — 8 vertical bars, height = band level
        const bars = Array.from({ length: 8 });
        return g(
          <g style={{
            transformBox: 'fill-box',
            transformOrigin: '50% 100%',
            transform: 'translate(calc(var(--bb-tilt-x, 0) * 3px), 0)',
          }}>
            {bars.map((_, i) => {
              const x = 10 + i * 10;
              return (
                <rect
                  key={i}
                  x={x}
                  y={56}
                  width={6}
                  height={40}
                  rx={0.6}
                  style={{
                    transformBox: 'fill-box',
                    transformOrigin: '50% 100%',
                    // Bar grows upward as the band loudens.
                    transform: `scaleY(calc(0.08 + var(--bb-band-${i}, 0.2) * 0.95))`,
                  }}
                />
              );
            })}
          </g>
        );
      }

      case 7: { // Vinyl Record — concentric grooves, rotates with energy.
        return g(
          <g style={{
            transformBox: 'fill-box',
            transformOrigin: '50% 75%',
            // Slow continuous rotation; faster on loud passages.
            animation: animate ? 'bb-vinyl-spin 14s linear infinite' : undefined,
            transform: 'translate(calc(var(--bb-tilt-x, 0) * 2px), calc(var(--bb-tilt-y, 0) * 2px))',
          }}>
            <circle cx={50} cy={75} r={20} />
            {[3, 6, 9, 12, 15, 18].map(r => (
              <circle key={r} cx={50} cy={75} r={r} opacity={0.45} />
            ))}
            <circle cx={50} cy={75} r={2.2} fill={stroke} stroke="none" />
            {/* Label dot — pulses with energy */}
            <circle
              cx={50}
              cy={75}
              r={5}
              fill="none"
              style={{
                transformBox: 'fill-box',
                transformOrigin: '50% 50%',
                transform: 'scale(calc(1 + var(--bb-energy, 0) * 0.6))',
              }}
            />
          </g>
        );
      }

      case 8: { // Piano Keys — 8 white keys, opacity glows with the band.
        return g(
          <g style={{ transform: 'translate(calc(var(--bb-tilt-x, 0) * 3px), 0)' }}>
            {Array.from({ length: 8 }).map((_, i) => {
              const x = 8 + i * 11;
              return (
                <rect
                  key={i}
                  x={x}
                  y={56}
                  width={10}
                  height={40}
                  style={{
                    // Brighten with band level. 0.25 base so the key shape is
                    // always legible; +0.7 ceiling on hits.
                    opacity: `calc(0.25 + var(--bb-band-${i}, 0.2) * 0.7)`,
                  }}
                />
              );
            })}
            {/* Black keys (5 of them, slot 1,2 — 4,5,6 — pattern) */}
            {[0, 1, 3, 4, 5].map((i) => {
              const x = 8 + i * 11 + 7.5;
              return <rect key={`b-${i}`} x={x} y={56} width={5} height={24} opacity={0.7} />;
            })}
          </g>
        );
      }

      case 9: { // Frequency Rings — 8 concentric rings sized by their band.
        return g(
          Array.from({ length: 8 }).map((_, i) => (
            <circle
              key={i}
              cx={50}
              cy={75}
              r={4 + i * 2.4}
              style={{
                transformBox: 'fill-box',
                transformOrigin: '50% 50%',
                // Each ring pulses on its own frequency band.
                transform: `scale(calc(0.85 + var(--bb-band-${i}, 0.2) * 0.45))`,
                opacity: `calc(0.25 + var(--bb-band-${i}, 0.2) * 0.6)`,
              }}
            />
          ))
        );
      }

      case 10: { // Cassette Reels — two spinning circles + tape line.
        return g(<>
          {/* Cassette outline */}
          <rect x={14} y={56} width={72} height={36} rx={3} opacity={0.5} />
          {[30, 70].map((cx, i) => (
            <g key={cx} style={{
              transformBox: 'fill-box',
              transformOrigin: `${cx}% 74%`,
              animation: animate ? `bb-vinyl-spin ${10 - i * 2}s linear infinite ${i % 2 ? 'reverse' : ''}` : undefined,
            }}>
              <circle cx={cx} cy={74} r={9} />
              <line x1={cx - 9} y1={74} x2={cx + 9} y2={74} />
              <line x1={cx} y1={65} x2={cx} y2={83} />
              <circle cx={cx} cy={74} r={2} fill={stroke} stroke="none" />
            </g>
          ))}
          {/* Tape window pulse */}
          <rect x={36} y={68} width={28} height={6} opacity="0.4"
                style={{
                  transformBox: 'fill-box', transformOrigin: '50% 50%',
                  transform: 'scaleX(calc(1 + var(--bb-energy, 0) * 0.4))',
                }} />
        </>);
      }

      case 11: { // Drum Pads — 4x2 grid, each pad lights on its band.
        return g(
          Array.from({ length: 8 }).map((_, i) => {
            const col = i % 4;
            const row = Math.floor(i / 4);
            const x = 14 + col * 18;
            const y = 60 + row * 18;
            return (
              <g key={i}>
                <rect x={x} y={y} width={14} height={14} rx={2} opacity={0.4} />
                <rect
                  x={x + 1.5}
                  y={y + 1.5}
                  width={11}
                  height={11}
                  rx={1.5}
                  fill={stroke}
                  stroke="none"
                  style={{
                    transformBox: 'fill-box',
                    transformOrigin: '50% 50%',
                    transform: `scale(calc(0.2 + var(--bb-band-${i}, 0.2) * 0.95))`,
                    opacity: `calc(0.3 + var(--bb-band-${i}, 0.2) * 0.7)`,
                  }}
                />
              </g>
            );
          })
        );
      }

      case 12: { // Sound Wave — sine path, amplitude swells with energy.
        return g(<>
          <path
            d="M 4 76 Q 17 56 30 76 T 56 76 T 82 76 T 108 76"
            style={{
              transformBox: 'fill-box',
              transformOrigin: '50% 76%',
              transform: 'scaleY(calc(0.5 + var(--bb-energy, 0) * 1.3))',
            }}
          />
          <path
            d="M 4 84 Q 17 64 30 84 T 56 84 T 82 84 T 108 84"
            opacity={0.55}
            style={{
              transformBox: 'fill-box',
              transformOrigin: '50% 84%',
              transform: 'scaleY(calc(0.4 + var(--bb-energy, 0) * 1.1))',
            }}
          />
          <line x1={4} y1={92} x2={96} y2={92} opacity={0.35} />
        </>);
      }

      case 13: { // Treble Clef — a stylised glyph that pulses with energy.
        return g(
          <g style={{
            transformBox: 'fill-box',
            transformOrigin: '50% 75%',
            transform: 'scale(calc(0.95 + var(--bb-energy, 0) * 0.18)) translate(calc(var(--bb-tilt-x, 0) * 4px), 0)',
          }}>
            {/* Staff */}
            {[64, 70, 76, 82, 88].map(y => (
              <line key={y} x1={6} y1={y} x2={94} y2={y} opacity={0.35} />
            ))}
            {/* Clef body — abstract spiral */}
            <path d="M 38 92 C 30 92 30 80 42 80 C 54 80 54 64 42 64 C 32 64 32 76 44 76 C 56 76 58 88 46 92" />
            <circle cx={38} cy={92} r={3} fill={stroke} stroke="none" />
            {/* Floating note heads — bob with bands */}
            <ellipse cx={58} cy={76} rx={3} ry={2.2}
              style={{ transformBox: 'fill-box', transformOrigin: '50% 50%', transform: 'translateY(calc(var(--bb-band-3, 0.2) * -4px))' }} />
            <ellipse cx={72} cy={82} rx={3} ry={2.2}
              style={{ transformBox: 'fill-box', transformOrigin: '50% 50%', transform: 'translateY(calc(var(--bb-band-5, 0.2) * -4px))' }} />
            <ellipse cx={86} cy={76} rx={3} ry={2.2}
              style={{ transformBox: 'fill-box', transformOrigin: '50% 50%', transform: 'translateY(calc(var(--bb-band-7, 0.2) * -4px))' }} />
          </g>
        );
      }

      case 14: { // Mixer Faders — 8 horizontal channels, knob position per band.
        return g(
          Array.from({ length: 8 }).map((_, i) => {
            const y = 58 + i * 4.5;
            return (
              <g key={i}>
                <line x1={10} y1={y} x2={90} y2={y} opacity={0.35} />
                {/* Knob — slides along the channel as the band level moves. */}
                <circle
                  cy={y}
                  r={2.4}
                  fill={stroke}
                  stroke="none"
                  style={{
                    // x = 10 + 80 * band; expressed as a transform on cx=10
                    transform: `translateX(calc(var(--bb-band-${i}, 0.2) * 80px))`,
                  }}
                  cx={10}
                />
              </g>
            );
          })
        );
      }

      case 15: { // Oscilloscope — abstract waveform line that breathes.
        return g(<>
          <rect x={6} y={56} width={88} height={38} opacity={0.35} />
          <path
            d="M 6 75 L 14 75 L 18 70 L 22 80 L 26 65 L 30 85 L 34 70 L 40 75 L 46 60 L 52 90 L 58 70 L 64 80 L 70 65 L 76 85 L 82 70 L 88 75 L 94 75"
            style={{
              transformBox: 'fill-box',
              transformOrigin: '50% 75%',
              transform: 'scaleY(calc(0.4 + var(--bb-energy, 0) * 1.6))',
            }}
          />
          {/* Sweep dot */}
          <circle
            cx={6} cy={75} r={1.6} fill={stroke} stroke="none"
            style={{ animation: animate ? 'bb-osc-sweep 2.2s linear infinite' : undefined }}
          />
        </>);
      }

      case 16: { // Speaker Cone — concentric rings of a driver. PUMPS with bass.
        return g(<>
          {/* Bass ripple — expands + fades on kick so the pump is unmissable */}
          <circle cx={50} cy={75} r={22}
            style={{
              transformBox: 'fill-box', transformOrigin: '50% 50%',
              transform: 'scale(calc(1 + var(--bb-band-0, 0) * 0.5))',
              opacity: 'calc(0.15 + var(--bb-band-0, 0) * 0.7)',
            }} />
          <circle cx={50} cy={75} r={22} opacity={0.4} />
          {/* Surround */}
          <circle cx={50} cy={75} r={18} opacity={0.65}
            style={{
              transformBox: 'fill-box', transformOrigin: '50% 50%',
              transform: 'scale(calc(0.92 + var(--bb-band-0, 0.2) * 0.4))',
            }} />
          {/* Cone */}
          <circle cx={50} cy={75} r={13}
            style={{
              transformBox: 'fill-box', transformOrigin: '50% 50%',
              transform: 'scale(calc(0.78 + var(--bb-band-1, 0.2) * 0.65))',
            }} />
          {/* Dust cap */}
          <circle cx={50} cy={75} r={6} fill={stroke} stroke="none"
            style={{
              transformBox: 'fill-box', transformOrigin: '50% 50%',
              transform: 'scale(calc(0.7 + var(--bb-energy, 0) * 0.9))',
            }} />
          {/* Mounting screws */}
          {[[28, 53], [72, 53], [28, 97], [72, 97]].map(([sx, sy], i) =>
            <circle key={i} cx={sx} cy={sy} r={1.5} fill={stroke} stroke="none" opacity={0.7} />
          )}
        </>);
      }

      case 17: { // Note Constellation — musical notes connected, drift with tilt.
        // Eight note positions scattered, lines connect them.
        const pts = Array.from({ length: 8 }).map((_, i) => ({
          x: 12 + ((seed >> i) % 76),
          y: 60 + ((seed >> (i + 3)) % 32),
          band: i,
        }));
        return g(
          <g style={{
            transform: 'translate(calc(var(--bb-tilt-x, 0) * 5px), calc(var(--bb-tilt-y, 0) * 5px))',
          }}>
            {/* Connecting lines */}
            {pts.map((p, i) => {
              const next = pts[(i + 1) % pts.length];
              return <line key={`l-${i}`} x1={p.x} y1={p.y} x2={next.x} y2={next.y} opacity={0.35} />;
            })}
            {/* Note heads — swell with their band */}
            {pts.map((p, i) => (
              <ellipse
                key={i}
                cx={p.x}
                cy={p.y}
                rx={2}
                ry={1.5}
                fill={stroke}
                stroke="none"
                style={{
                  transformBox: 'fill-box',
                  transformOrigin: '50% 50%',
                  transform: `scale(calc(0.7 + var(--bb-band-${p.band}, 0.2) * 1.3))`,
                }}
              />
            ))}
            {/* Stems */}
            {pts.slice(0, 4).map((p, i) => (
              <line key={`s-${i}`} x1={p.x + 2} y1={p.y} x2={p.x + 2} y2={p.y - 8} opacity={0.55} />
            ))}
          </g>
        );
      }

      default:
        return null;
    }
  };

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid slice"
      width={size}
      height={size}
      role="img"
      aria-label={name || undefined}
      className={className}
      style={{ display: 'block' }}
    >
      {/* Soft glow behind every pattern stroke — makes the vector art pop
          without changing its geometry. Referenced by renderPattern()'s g(). */}
      <defs>
        <filter id={`pglow-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="0.7" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Background — transparent by default per Gordon's spec. Painted only
          when the caller sets `bgColor` to "theme" or a custom CSS colour. */}
      {bg !== 'transparent' && <rect width="100" height="100" fill={bg} />}

      {/* Vector composition — bottom half, stroked in fgColor (default
          theme foreground). Switches by pattern index. */}
      {renderPattern()}

      {/* Artist name — Impact, stretched to fill width, sitting at the top.
          NO underline. Halo stroke matches the bg so text reads on any fill. */}
      {lines.map((line, i) => (
        <text
          key={i}
          x="50"
          y={ys[i]}
          textAnchor="middle"
          fontFamily={fontStack}
          fontWeight={900}
          fontSize={fontSize}
          fill={stroke}
          stroke={textHaloStroke}
          strokeWidth={0.4}
          paintOrder="stroke fill"
          textLength={88}
          lengthAdjust="spacingAndGlyphs"
          style={{ letterSpacing: '-0.02em' }}
        >
          {line}
        </text>
      ))}

      {/* Local keyframes used by the musical patterns. Scoped via the SVG so
          they don't leak globally. */}
      {animate && (
        <style>{`
          @keyframes bb-vinyl-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
          @keyframes bb-osc-sweep { from { transform: translateX(0); opacity: 1; }
                                    99% { transform: translateX(88px); opacity: 1; }
                                    100% { transform: translateX(0); opacity: 0; } }
        `}</style>
      )}

      {/* uid kept to avoid React `useId` ESLint moan when nothing else
          references it. */}
      <metadata data-uid={uid} />
    </svg>
  );
}
